/**
 * Resilient local mic/camera enable helpers for LiveKit.
 * Avoids stale deviceId ("Requested device not found") and clarifies permission errors.
 */

import {
  createLocalAudioTrack,
  createLocalVideoTrack,
  Room,
  Track,
  type LocalTrack,
  type Room as LiveKitRoom,
} from "livekit-client";
import type { LocalParticipant } from "livekit-client";

export type MediaKind = "audio" | "video" | "screen";

function errorName(err: unknown): string {
  if (err && typeof err === "object" && "name" in err) {
    return String((err as { name?: string }).name || "");
  }
  return "";
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err || "");
}

export function formatMediaError(err: unknown, kind: MediaKind): string {
  const name = errorName(err);
  const message = errorMessage(err);
  const notFound =
    name === "NotFoundError" ||
    name === "OverconstrainedError" ||
    /requested device not found|device not found|no device|overconstrained/i.test(
      message,
    );
  const denied =
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    /permission|denied|blocked|not allowed/i.test(message);

  if (kind === "audio") {
    if (notFound) {
      return "No microphone found. Plug in a mic (or enable it in Windows Sound settings), then click Unmute again.";
    }
    if (denied) {
      return "Microphone permission blocked. Click the lock/camera icon in the browser address bar, allow Microphone, then Unmute.";
    }
    return message || "Could not access microphone.";
  }

  if (kind === "video") {
    if (notFound) {
      return "No camera found. Connect a webcam, then click Start Camera again.";
    }
    if (denied) {
      return "Camera permission blocked. Allow Camera in the browser address bar, then Start Camera.";
    }
    return message || "Could not access camera.";
  }

  if (denied) {
    return "Screen share was blocked or cancelled.";
  }
  return message || "Could not start screen share.";
}

async function getInputDevices(
  kind: "audioinput" | "videoinput",
): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices) return [];
  try {
    const devices = await Room.getLocalDevices(kind, true);
    return devices.filter(
      (device) => Boolean(device.deviceId) && device.deviceId !== "default",
    );
  } catch {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter(
        (device) =>
          device.kind === kind &&
          Boolean(device.deviceId) &&
          device.deviceId !== "default",
      );
    } catch {
      return [];
    }
  }
}

async function publishOrEnable(
  localParticipant: LocalParticipant,
  track: LocalTrack,
  source: Track.Source,
): Promise<void> {
  const existing = localParticipant.getTrackPublication(source);
  if (existing?.track) {
    track.stop();
    if (source === Track.Source.Microphone) {
      await localParticipant.setMicrophoneEnabled(true);
    } else if (source === Track.Source.Camera) {
      await localParticipant.setCameraEnabled(true);
    }
    return;
  }
  await localParticipant.publishTrack(track, { source });
}

/**
 * Enable microphone:
 * 1) unmute existing publication if present
 * 2) LiveKit setMicrophoneEnabled (no deviceId)
 * 3) createLocalAudioTrack + publish as Microphone
 * 4) try each real audioinput deviceId
 */
export async function enableLocalMicrophone(
  localParticipant: LocalParticipant,
  room?: LiveKitRoom,
): Promise<void> {
  const existing = localParticipant.getTrackPublication(Track.Source.Microphone);
  if (existing?.track) {
    await localParticipant.setMicrophoneEnabled(true);
    return;
  }

  try {
    await localParticipant.setMicrophoneEnabled(true);
    return;
  } catch {
    // fall through to explicit track create/publish
  }

  try {
    const track = await createLocalAudioTrack({
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    });
    await publishOrEnable(
      localParticipant,
      track,
      Track.Source.Microphone,
    );
    return;
  } catch {
    // try per-device next
  }

  const audioInputs = await getInputDevices("audioinput");
  let lastError: unknown = new Error("No microphone found");

  for (const device of audioInputs) {
    try {
      if (room) {
        try {
          await room.switchActiveDevice("audioinput", device.deviceId, false);
        } catch {
          // ignore switch failures; still try capture with this id
        }
      }
      const track = await createLocalAudioTrack({
        deviceId: { ideal: device.deviceId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      });
      await publishOrEnable(
        localParticipant,
        track,
        Track.Source.Microphone,
      );
      return;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError;
}

export async function enableLocalCamera(
  localParticipant: LocalParticipant,
  room?: LiveKitRoom,
): Promise<void> {
  const existing = localParticipant.getTrackPublication(Track.Source.Camera);
  if (existing?.track) {
    await localParticipant.setCameraEnabled(true);
    return;
  }

  try {
    await localParticipant.setCameraEnabled(true);
    return;
  } catch {
    // fall through
  }

  try {
    const track = await createLocalVideoTrack({ facingMode: "user" });
    await publishOrEnable(localParticipant, track, Track.Source.Camera);
    return;
  } catch {
    // try per-device next
  }

  const videoInputs = await getInputDevices("videoinput");
  let lastError: unknown = new Error("No camera found");

  for (const device of videoInputs) {
    try {
      if (room) {
        try {
          await room.switchActiveDevice("videoinput", device.deviceId, false);
        } catch {
          // ignore
        }
      }
      const track = await createLocalVideoTrack({
        deviceId: { ideal: device.deviceId },
        facingMode: "user",
      });
      await publishOrEnable(localParticipant, track, Track.Source.Camera);
      return;
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError;
}
